/**
 * PATH: packages/ui/src/kanban/api-fetch/proposeRuleRequestStream.ts
 * FILE_ID: UI-KANBAN-FETCH-RULE-REQ-STREAM-001
 * PURPOSE: C-01 — POST /rule-requests/propose-stream/ and fold SSE into
 *          RuleRequestProposeResult (same shape as JSON propose).
 */
import type { RuleRequestProposeInput, RuleRequestProposeResult } from '../api'
import { KanbanNetworkError } from '../api'

type SseEv = {
  type: string
  preview_id?: string | null
  payload?: Record<string, unknown>
}

function parseSseBlocks(buffer: string): { events: SseEv[]; rest: string } {
  const parts = buffer.split('\n\n')
  const rest = parts.pop() ?? ''
  const events: SseEv[] = []
  for (const block of parts) {
    let dataLine = ''
    let eventType = 'message'
    for (const line of block.split('\n')) {
      if (line.startsWith('data:')) dataLine = line.slice(5).trim()
      if (line.startsWith('event:')) eventType = line.slice(6).trim()
    }
    if (!dataLine) continue
    try {
      const parsed = JSON.parse(dataLine) as SseEv
      events.push({
        type: parsed.type || eventType,
        preview_id: parsed.preview_id,
        payload: parsed.payload ?? {},
      })
    } catch {
      // ignore malformed chunk
    }
  }
  return { events, rest }
}

export async function proposeRuleRequestViaStream(
  opts: {
    baseUrl: string
    getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>
  },
  input: RuleRequestProposeInput,
): Promise<RuleRequestProposeResult> {
  const url = `${opts.baseUrl.replace(/\/$/, '')}/rule-requests/propose-stream/`
  const auth = opts.getAuthHeaders ? await opts.getAuthHeaders() : {}
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...auth,
    },
    body: JSON.stringify({
      organisation_id: input.organisationId,
      instruction: input.instruction,
      ...(input.emailId ? { email_id: input.emailId } : {}),
    }),
  })
  if (!res.ok || !res.body) {
    throw new Error(`propose-stream failed (${res.status})`)
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let result: RuleRequestProposeResult | null = null
  let streamError: string | null = null
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const parsed = parseSseBlocks(buf)
    buf = parsed.rest
    for (const ev of parsed.events) {
      const p = ev.payload || {}
      if (ev.type === 'error') {
        streamError = String(p.error || 'propose-stream error')
      }
      const previewId = String(p.preview_id || ev.preview_id || '')
      const proposal = p.proposal
      if (ev.type === 'preview' && previewId && proposal) {
        result = {
          proposal: proposal as RuleRequestProposeResult['proposal'],
          preview_id: previewId,
          confirmation_token: String(p.confirmation_token || ''),
          expires_at: String(p.expires_at || ''),
          matched_existing: (p.matched_existing as RuleRequestProposeResult['matched_existing']) || {
            count: 0,
            scanned: 0,
            sample: [],
          },
        }
      }
    }
  }
  if (result) return result
  throw new KanbanNetworkError(
    streamError || 'propose-stream ended without preview',
  )
}
